import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import api from '../../lib/api';
import { qk } from '../auth/api';

type NotesInboxRow = {
  orderId: string;
  customerId: string | null;
  customerName: string;
  loadNumber: string | null;
  latestNoteAt: string;
  latestNotePreview: string;
  unseenCount: number;
};

type OrderNotesInboxPage = {
  rows: NotesInboxRow[];
  nextCursor: string | null;
};

export type OrderNotesSort = 'recent' | 'oldest';

export type OrderNotesInboxParams = {
  assignment: string;
  search?: string;
  unseenOnly?: boolean;
  sort?: OrderNotesSort;
};

export type BulkAddOrderNotesVars = {
  orderIds: string[];
  body: string;
};

export type BulkAddOrderNotesResponse = {
  ok: true;
  createdCount: number;
  orderIds: string[];
};

export function useOrderNotesInbox(params: OrderNotesInboxParams) {
  const { assignment, search = '', unseenOnly = false, sort = 'recent' } = params;

  return useInfiniteQuery<OrderNotesInboxPage>({
    // nextCursor is an offset; pages start at 0.
    queryKey: ['org:order-notes:inbox', assignment, search, unseenOnly, sort],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const { data } = await api.get<OrderNotesInboxPage>('/organization/order-notes/inbox', {
        params: {
          assignment,
          offset: pageParam,
          take: 20,
          sort,
          ...(search ? { search } : {}),
          ...(unseenOnly ? { unseenOnly: true } : {}),
        },
      });

      return data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.nextCursor != null ? Number(lastPage.nextCursor) : undefined,
  });
}

type OrderNoteDetail = {
  id: string;
  body: string;
  createdAt: string;
  authorName: string;
  authorType: string;
  seen: boolean;
};

type OrderNotesDetailPage = {
  order: {
    id: string;
    customerName: string;
    loadNumber: string | null;
  };
  notes: OrderNoteDetail[];
  nextCursor: string | null;
};

export function useOrderNotesDetail(orderId?: string) {
  return useInfiniteQuery<OrderNotesDetailPage>({
    queryKey: ['org:order-notes:detail', orderId],
    enabled: Boolean(orderId),
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const { data } = await api.get<OrderNotesDetailPage>(
        `/organization/order-notes/orders/${orderId}/notes`,
        {
          params: {
            cursor: pageParam,
            take: 30,
          },
        }
      );

      return data;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}

export function useMarkOrderNotesSeen() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (orderId: string) => {
      const { data } = await api.post(
        `/organization/order-notes/orders/${orderId}/notes/mark-seen`
      );

      return data;
    },
    onSuccess: (_data, orderId) => {
      void qc.invalidateQueries({
        queryKey: ['org:order-notes:inbox'],
      });

      void qc.invalidateQueries({
        queryKey: ['org:order-notes:detail', orderId],
      });

      void qc.invalidateQueries({
        queryKey: qk.me,
      });
    },
  });
}

export function useBulkAddOrderNotes() {
  const qc = useQueryClient();

  return useMutation<BulkAddOrderNotesResponse, unknown, BulkAddOrderNotesVars>({
    mutationFn: async (payload) => {
      const { data } = await api.post<BulkAddOrderNotesResponse>(
        '/organization/order-notes/orders/notes/bulk',
        payload
      );

      return data;
    },
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: ['org:order-notes:inbox'],
      });

      for (const orderId of variables.orderIds) {
        void qc.invalidateQueries({
          queryKey: ['org:order-notes:detail', orderId],
        });
      }

      void qc.invalidateQueries({
        queryKey: ['org:orders:items'],
      });

      void qc.invalidateQueries({
        queryKey: qk.me,
      });
    },
  });
}
